import { Controller } from "@hotwired/stimulus"

// Sync Notification Controller - Manages notification behavior
export default class extends Controller {
  static targets = ["container"]
  static values = {
    autoDismiss: { type: Boolean, default: true },
    dismissAfter: { type: Number, default: 8000 } // 8 seconds
  }

  connect() {
    console.log("Sync notification controller connected")
    
    // Auto-dismiss after delay for non-critical notifications
    if (this.autoDismissValue && !this.isFailedSync()) {
      this.autoDismissTimer = setTimeout(() => {
        this.dismiss()
      }, this.dismissAfterValue)
    }
  }

  disconnect() {
    if (this.autoDismissTimer) {
      clearTimeout(this.autoDismissTimer)
    }
  }

  dismiss() {
    this.element.style.transform = 'translateX(100%)'
    this.element.style.opacity = '0'
    
    setTimeout(() => {
      if (this.element.parentNode) {
        this.element.remove()
      }
    }, 300)
  }

  handleStatusUpdate(event) {
    const { status } = event.detail
    
    // Hide notification when sync completes successfully
    if (status === 'synced') {
      this.showSuccessAndDismiss()
    }
    // Update notification for failed status
    else if (status === 'failed') {
      this.updateToFailedState()
    }
  }

  showSuccessAndDismiss() {
    // Briefly show success state
    this.updateNotificationState({
      classes: 'bg-green-50 border-green-200 text-green-800',
      iconClasses: 'text-green-500',
      icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
      title: 'Successfully Synced',
      message: 'Your application has been added to our CRM system.',
      animate: false
    })

    // Dismiss after showing success
    setTimeout(() => {
      this.dismiss()
    }, 3000)
  }

  updateToFailedState() {
    this.updateNotificationState({
      classes: 'bg-red-50 border-red-200 text-red-800',
      iconClasses: 'text-red-500',
      icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.96-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z',
      title: 'Sync Issue Detected',
      message: 'There was a temporary issue adding your application to our system. Our team has been notified.',
      animate: false
    })

    // Don't auto-dismiss failed notifications
    if (this.autoDismissTimer) {
      clearTimeout(this.autoDismissTimer)
      this.autoDismissTimer = null
    }
  }

  updateNotificationState(config) {
    const container = this.containerTarget
    
    // Update classes
    container.className = `${config.classes} border rounded-lg shadow-lg p-4 transition-all duration-300 ${config.animate ? 'animate-pulse' : ''}`
    
    // Update icon
    const icon = container.querySelector('svg')
    if (icon) {
      icon.className = `w-5 h-5 ${config.iconClasses} ${config.animate ? 'animate-spin' : ''}`
      const path = icon.querySelector('path')
      if (path) {
        path.setAttribute('d', config.icon)
      }
    }
    
    // Update title
    const title = container.querySelector('.font-medium')
    if (title) {
      title.textContent = config.title
    }
    
    // Update message
    const message = container.querySelector('.text-sm.mt-1')
    if (message) {
      message.textContent = config.message
    }
    
    // Remove progress indicator if present
    const progress = container.querySelector('.bg-yellow-200')
    if (progress) {
      progress.parentElement.remove()
    }
  }

  isFailedSync() {
    return this.element.querySelector('.bg-red-50') !== null
  }
}